import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Question from "../../components/Question";
import MoveButton from "../../components/MoveButton";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";
import { setPhq9Answer } from "../../redux/userSlice";

const PHQ9_QUESTIONS = [
  { key: "q1_little_interest", label: "Little interest or pleasure in doing things" },
  { key: "q2_feeling_down", label: "Feeling down, depressed, or hopeless" },
  { key: "q3_sleep", label: "Trouble falling or staying asleep, or sleeping too much" },
  { key: "q4_tired", label: "Feeling tired or having little energy" },
  { key: "q5_appetite", label: "Poor appetite or overeating" },
  {
    key: "q6_feeling_bad_about_self",
    label:
      "Feeling bad about yourself — or that you are a failure or have let yourself or your family down",
  },
  {
    key: "q7_concentration",
    label:
      "Trouble concentrating on things, such as reading the newspaper or watching television",
  },
  {
    key: "q8_psychomotor",
    label:
      "Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual",
  },
  {
    key: "q9_self_harm",
    label: "Thoughts that you would be better off dead or of hurting yourself in some way",
  },
];

const ANSWER_CHOICES = [
  { label: "Not at All", value: 0 },
  { label: "Several Days", value: 1 },
  { label: "More than half the Days", value: 2 },
  { label: "Nearly every day", value: 3 },
];

export default function Phq9Questionnaire() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

  const user = useSelector((state) => state.user);
  const phq9 = user.phq9 || {};

  const [step, setStep] = useState(0);
  const current = PHQ9_QUESTIONS[step];
  const currentValue = phq9?.[current.key];

  const isLast = step === PHQ9_QUESTIONS.length - 1;

  const handleSelect = useCallback(
    (value) => {
      dispatch(setPhq9Answer({ key: current.key, value }));

      if (!isLast) {
        setStep((s) => s + 1);
      } else {
        if (user.sex.sexAtBirth === "Female" || user.sex.sexAtBirth === "Prefer not to answer") {
          setPageAndDeferNavigate(dispatch, "/maternity", () =>
            navigate("/maternity", { state: { returning: true } })
          );
        } else {
          setPageAndDeferNavigate(dispatch, '/recent-photo', () =>
                navigate('/recent-photo', { state: { returning: true } })
            );
        }
      }
    },
    [dispatch, current.key, isLast, dispatch, navigate]
  );

  const handleBack = useCallback(() => {
    if (step > 0) {
      setStep((s) => s - 1);
      return;
    }
    setPageAndDeferNavigate(dispatch, "/side-effects-checklist", navigate);
  }, [step, dispatch, navigate]);

  return (
    <Question
      onBack={handleBack}
      returning={true}
      title="How have you been feeling?"
      subtitle="Over the last 2 weeks, how often have you been bothered by the following..."
      input={
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Box sx={{ width: "100%", maxWidth: 560 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                mb: 2,
              }}
            >
              <CardContent sx={{ p: isSmall ? 2 : 3 }}>
                <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2 }}>
                  {current.label}
                </Typography>
              </CardContent>
            </Card>

            <Box key={current.key} display="flex" flexDirection="column" alignItems="center" width="100%">
              <Stack spacing={2} width="100%" maxWidth="sm" alignItems="center">
                {ANSWER_CHOICES.map((c) => (
                  <MoveButton
                    key={`${current.key}-${c.value}`}
                    onClick={() => handleSelect(c.value)}
                    title={c.label}
                    selected={currentValue === c.value}
                    fullWidth
                    sx={{
                      width: "100%",
                      maxWidth: isDesktop ? "400px" : "100%",
                    }}
                  />
                ))}
              </Stack>
            </Box>

            <Typography
              sx={{
                mt: 2,
                fontSize: 11,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                textAlign: "center",
                opacity: 0.6,
              }}
            >
              Based on PHQ-9 mental health standards
            </Typography>
          </Box>
        </Box>
      }
    />
  );
}
